/**
 * CameraPreview - カメラプレビューコンポーネント
 */

import { useRef, useEffect, forwardRef } from 'react'

interface CameraPreviewProps {
  isActive?: boolean
  showGrid?: boolean
  showGhost?: boolean
  ghostImage?: string
  aspectRatio?: '4:3' | '16:9' | '1:1'
  orientation?: 'portrait' | 'landscape'
  onVideoReady?: (video: HTMLVideoElement) => void
}

export const CameraPreview = forwardRef<HTMLVideoElement, CameraPreviewProps>(
  ({ 
    isActive = false,
    showGrid = false,
    showGhost = false,
    ghostImage,
    aspectRatio = '16:9',
    orientation = 'portrait',
    onVideoReady
  }, ref) => {
    const internalRef = useRef<HTMLVideoElement>(null)
    const videoRef = (ref as React.RefObject<HTMLVideoElement>) || internalRef

    useEffect(() => {
      if (videoRef.current && onVideoReady) {
        onVideoReady(videoRef.current)
      }
    }, [onVideoReady, videoRef])

    // アスペクト比のクラスを決定
    const getAspectRatioClass = () => {
      if (orientation === 'landscape') {
        return 'aspect-video' // 16:9
      }
      
      switch (aspectRatio) {
        case '4:3':
          return 'aspect-[4/3]'
        case '1:1':
          return 'aspect-square'
        case '16:9':
        default:
          return 'aspect-[9/16]' // 縦向きの16:9
      }
    }

    return (
      <div className="relative w-full h-full bg-black flex items-center justify-center">
        {/* ビデオ要素 */}
        <div className={`relative w-full ${getAspectRatioClass()} max-h-full`}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover rounded-lg"
          />

          {/* ゴースト画像オーバーレイ */}
          {showGhost && ghostImage && (
            <div className="absolute inset-0 pointer-events-none">
              <img
                src={ghostImage}
                alt="前回の撮影"
                className="w-full h-full object-cover opacity-30 rounded-lg"
              />
            </div>
          )}

          {/* グリッドオーバーレイ */}
          {showGrid && (
            <div className="absolute inset-0 pointer-events-none">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {/* 縦線 */}
                <line x1="33.33" y1="0" x2="33.33" y2="100" stroke="white" strokeWidth="0.2" opacity="0.5" />
                <line x1="66.66" y1="0" x2="66.66" y2="100" stroke="white" strokeWidth="0.2" opacity="0.5" />
                {/* 横線 */}
                <line x1="0" y1="33.33" x2="100" y2="33.33" stroke="white" strokeWidth="0.2" opacity="0.5" />
                <line x1="0" y1="66.66" x2="100" y2="66.66" stroke="white" strokeWidth="0.2" opacity="0.5" />
              </svg>
            </div>
          )}

          {/* カメラ非アクティブ時の表示 */}
          {!isActive && (
            <div className="absolute inset-0 bg-gray-900 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-gray-500">カメラを起動してください</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)

CameraPreview.displayName = 'CameraPreview'